/**
 * String utility helpers for display formatting
 */

/**
 * UUID v4 regex pattern - matches standard UUID format
 * Compiled once at module level for performance
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Check if a string looks like a GUID/UUID
 * Used to detect when internal identifiers are being displayed instead of user-friendly values
 *
 * @param str - The string to check
 * @returns true if the string matches UUID v4 format
 */
export const isGuid = (str: string): boolean => {
  return UUID_PATTERN.test(str);
};

/**
 * Get display name for a creator, with fallback for empty or GUID-like values
 *
 * @param creator - The creator string from the API
 * @returns User-friendly display name, defaulting to "Unknown Artist"
 */
export const getCreatorDisplayName = (creator?: string | null): string => {
  if (!creator) {
    return 'Unknown Artist';
  }

  const trimmed = creator.trim();

  // Treat empty, GUID-like, or "anon" as invalid creator names
  if (!trimmed || isGuid(trimmed) || trimmed.toLowerCase() === 'anon') {
    return 'Unknown Artist';
  }

  return trimmed;
};

/**
 * Get display name for a catalog, with fallback for empty or GUID-like values
 *
 * @param catalogName - The catalog name from the API
 * @returns User-friendly display name, defaulting to "Unnamed Catalog"
 */
export const getCatalogDisplayName = (catalogName?: string | null): string => {
  if (!catalogName) {
    return 'Unnamed Catalog';
  }

  const trimmed = catalogName.trim();

  // Check for empty, GUID-like, or names that contain only a GUID
  if (!trimmed || isGuid(trimmed) || containsOnlyGuid(trimmed)) {
    return 'Unnamed Catalog';
  }

  return trimmed;
};

/**
 * Check if a string contains only a GUID (possibly with surrounding whitespace or "Catalog" prefix)
 * This catches cases like "Catalog 550e8400-e29b-41d4-a716-446655440000" or similar
 */
export const containsOnlyGuid = (str: string): boolean => {
  // Remove common prefixes like "Catalog" or "catalog-"
  const cleaned = str.replace(/^(catalog[-_\s]*)/i, '').trim();
  return isGuid(cleaned);
};
